// core/services/fullscreen.service.ts
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class FullscreenService {
  private renderer: Renderer2;
  private currentFullscreenElementSubject = new BehaviorSubject<HTMLElement | null>(null);
  public currentFullscreenElement$: Observable<HTMLElement | null> = this.currentFullscreenElementSubject.asObservable();
  
  private cssFullscreenElement: HTMLElement | null = null;
  private isFullscreenSupported: boolean;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
    
    // Check if Fullscreen API is supported
    this.isFullscreenSupported = !!(
      document.fullscreenEnabled ||
      (document as any).webkitFullscreenEnabled ||
      (document as any).mozFullScreenEnabled ||
      (document as any).msFullscreenEnabled
    );
    
    document.addEventListener('fullscreenchange', () => {
      this.currentFullscreenElementSubject.next(document.fullscreenElement as HTMLElement | null);
    });
  }

  initializeFullscreenButtons() {
    const buttons = document.querySelectorAll('.fullscreen-button');
    buttons.forEach((button) => {
      this.renderer.listen(button, 'click', () => {
        const card = (button as HTMLElement).closest('.card') as HTMLElement;
        if (card) {
          this.toggle(card);
        }
      });
    });
  }

  toggle(element: HTMLElement) {
    // Check if already in fullscreen (either native or CSS)
    const isCurrentlyFullscreen = document.fullscreenElement === element || this.cssFullscreenElement === element;
    
    if (!isCurrentlyFullscreen) {
      // Try native fullscreen first
      if (this.isFullscreenSupported && element.requestFullscreen) {
        element.requestFullscreen()
          .then(() => {
            this.currentFullscreenElementSubject.next(element);
          })
          .catch((err) => {
            // Fallback to CSS fullscreen if native fails (common on PWA tablets)
            this.applyCSSFullscreen(element);
          });
      } else {
        // Use CSS fullscreen fallback
        this.applyCSSFullscreen(element);
      }
    } else {
      // Exit fullscreen
      if (document.fullscreenElement) {
        document.exitFullscreen().then(() => {
          this.currentFullscreenElementSubject.next(null);
        });
      } else {
        this.removeCSSFullscreen();
      }
    }
  }

  private applyCSSFullscreen(element: HTMLElement) {
    this.cssFullscreenElement = element;
    
    // Store original styles
    const originalPosition = element.style.position;
    const originalZIndex = element.style.zIndex;
    const originalWidth = element.style.width;
    const originalHeight = element.style.height;
    const originalTop = element.style.top;
    const originalLeft = element.style.left;
    
    // Apply fullscreen styles
    this.renderer.setStyle(element, 'position', 'fixed');
    this.renderer.setStyle(element, 'top', '0');
    this.renderer.setStyle(element, 'left', '0');
    this.renderer.setStyle(element, 'width', '100vw');
    this.renderer.setStyle(element, 'height', '100vh');
    this.renderer.setStyle(element, 'zIndex', '9999');
    
    // Add fullscreen class for CSS targeting
    this.renderer.addClass(element, 'css-fullscreen');
    
    // Hide body scroll
    this.renderer.setStyle(document.body, 'overflow', 'hidden');
    
    // Store original styles for restoration
    (element as any)._fullscreenOriginalStyles = {
      position: originalPosition,
      zIndex: originalZIndex,
      width: originalWidth,
      height: originalHeight,
      top: originalTop,
      left: originalLeft,
    };
    
    this.currentFullscreenElementSubject.next(element);
  }

  private removeCSSFullscreen() {
    if (this.cssFullscreenElement) {
      const styles = (this.cssFullscreenElement as any)._fullscreenOriginalStyles;
      
      // Remove fullscreen class
      this.renderer.removeClass(this.cssFullscreenElement, 'css-fullscreen');
      
      if (styles) {
        this.renderer.setStyle(this.cssFullscreenElement, 'position', styles.position);
        this.renderer.setStyle(this.cssFullscreenElement, 'zIndex', styles.zIndex);
        this.renderer.setStyle(this.cssFullscreenElement, 'width', styles.width);
        this.renderer.setStyle(this.cssFullscreenElement, 'height', styles.height);
        this.renderer.setStyle(this.cssFullscreenElement, 'top', styles.top);
        this.renderer.setStyle(this.cssFullscreenElement, 'left', styles.left);
      }
      
      this.renderer.setStyle(document.body, 'overflow', '');
      this.cssFullscreenElement = null;
    }
    
    this.currentFullscreenElementSubject.next(null);
  }
}
